import { Box, Button } from "@mui/material";
import { DialogModal, Drawer } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import type { ImmovableAssetItemImmovableSubType, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useRef, useState } from "react";
import { ImmovableContractForm } from "./ImmovableContractForm";
import { MovableContractOtherForm } from "./MovableContractOtherForm";
import { MovableContractVehicleForm } from "./MovableContractVehicleForm";

type Props = {
  readonly assetContractId?: string;
  readonly assetItemId: string;
  readonly assetType: "immovable" | "movable";
  readonly assetSubType: ImmovableAssetItemImmovableSubType | MovableAssetItemMovableSubType;
  readonly contractType?: "current" | "proposed";
  readonly onClose: () => void;
  readonly open: boolean;
};

export type SubmitContractFormHandle = {
  readonly submitForm: () => void;
  readonly delete: () => void;
};

export const ContractDrawer: FC<Props> = ({ onClose, open, assetType, assetItemId, assetSubType, assetContractId, contractType }) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);

  const [pending, setPending] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const immovableContractFormRef = useRef<SubmitContractFormHandle>(null);
  const movableContractFormRef = useRef<SubmitContractFormHandle>(null);
  const vehicleContractFormRef = useRef<SubmitContractFormHandle>(null);

  const isVehicle = assetType === "movable" && assetSubType === "vehicle";
  const isMovableOther = assetType === "movable" && (assetSubType === "other" || assetSubType === "company");

  const activeFormRef = assetType === "immovable" ? immovableContractFormRef : isVehicle ? vehicleContractFormRef : movableContractFormRef;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        title={t(
          assetContractId
            ? contractType === "current"
              ? Resources.AnalysisDetail.AssetsAndContracts.EditExistingContract
              : Resources.AnalysisDetail.AssetsAndContracts.EditNewContract
            : Resources.AnalysisDetail.AssetsAndContracts.AddContract,
        )}
        startAction={
          <Button variant="text" color="error" disabled={readOnly || pending || !assetContractId} onClick={() => setDeleteModalOpen(true)}>
            {t(Resources.Common.Delete)}
          </Button>
        }
        primaryAction={
          <Button
            variant="contained"
            disabled={readOnly || pending}
            type="submit"
            onClick={() => {
              activeFormRef.current?.submitForm();
            }}
          >
            {t(Resources.Common.Save)}
          </Button>
        }
      >
        <Box mb={Spacing.XXL}>
          {assetType === "immovable" && (
            <ImmovableContractForm
              ref={immovableContractFormRef}
              assetContractId={assetContractId}
              assetItemId={assetItemId}
              onClose={onClose}
              onPendingChange={setPending}
            />
          )}
          {isMovableOther && (
            <MovableContractOtherForm
              ref={movableContractFormRef}
              assetItemId={assetItemId}
              assetContractId={assetContractId}
              onClose={onClose}
              onPendingChange={setPending}
            />
          )}
          {isVehicle && (
            <MovableContractVehicleForm
              ref={vehicleContractFormRef}
              assetItemId={assetItemId}
              assetContractId={assetContractId}
              onClose={onClose}
              onPendingChange={setPending}
            />
          )}
        </Box>
      </Drawer>
      <DialogModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        closeLabel={t(Resources.Common.Close)}
        title={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteContractTitle)}
        subtitle={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteContractText)}
        primaryAction={
          <Button
            variant="contained"
            disabled={readOnly || pending}
            onClick={() => {
              setDeleteModalOpen(false);
              activeFormRef.current?.delete();
            }}
          >
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </>
  );
};
